import { GrowArea } from './GrowArea';
import { PinnedSheet } from './SetSheet';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { alert, haptic } from '../lib/telegram';
import { getLang, useT } from '../lib/i18n';

/**
 * The skills a coach rates, and what each band of the scale means.
 *
 * A skill is the coach's own word, so it is kept in both languages the
 * app speaks and never translated on the fly: the coach writes one name,
 * asks for the other, and edits whatever comes back. The guide is the
 * same idea one level down — one line per band, in the coach's words,
 * shown to whoever is reading in their own language.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setAxisToken = (t: string) => { authToken = t; };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface Guide { band: number; en: string | null; ru: string | null }
export interface Axis { id: string; name: string; name_ru: string | null; sort_order: number; guides: Guide[]; sport?: 'padel' | 'tennis' }
interface SkillMap {
  shared: { key: string; name_ru: string; name_en: string }[];
  axes: { id: string; shared_skill: string | null; source: 'ai' | 'coach' | null }[];
}

/** The bands a 1-10 score falls into, by their lower bound. */
export const BANDS: [number, 'co.rate.w1' | 'co.rate.w3' | 'co.rate.w5' | 'co.rate.w7' | 'co.rate.w9'][] = [
  [1, 'co.rate.w1'], [3, 'co.rate.w3'], [5, 'co.rate.w5'], [7, 'co.rate.w7'], [9, 'co.rate.w9'],
];

/** The line for a score, in the reader's language, or nothing. */
export function guideFor(axis: { guides?: Guide[] }, score: number, lang: 'en' | 'ru') {
  if (score < 1) return null;
  const band = Math.min(9, Math.floor((score - 1) / 2) * 2 + 1);
  const g = (axis.guides ?? []).find((x) => x.band === band);
  if (!g) return null;
  return (lang === 'ru' ? g.ru || g.en : g.en || g.ru) || null;
}

/**
 * Settings → «Навыки»: the list in a sheet, opened from one row. Tap a
 * skill to edit it; «+ Новый навык» adds one, saved with the pinned
 * «Сохранить».
 */
export function AxesSheet({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [axes, setAxes] = useState<Axis[] | null>(null);
  const [editing, setEditing] = useState<Axis | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // A coach who teaches tennis too sees one sport's skills at a time, and a
  // new skill goes to that sport. Tennis skills exist from the moment tennis
  // is turned on (coach/sports.ts), so their presence is the sign.
  const [sport, setSport] = useState<'padel' | 'tennis'>('padel');
  // The coach's sports (Settings): both → a switch; one → just that sport's skills.
  const [coachSports, setCoachSports] = useState<string[]>(['padel']);
  useEffect(() => {
    api<{ coach: { sports?: string[] } }>('/api/coach/settings').then((d) => {
      const has = d.coach.sports ?? ['padel'];
      setCoachSports(has);
      if (!has.includes('padel')) setSport('tennis');
    }).catch(() => {});
  }, []);
  const both = coachSports.length > 1;
  const shown = axes?.filter((a) => !both || (a.sport ?? 'padel') === sport) ?? null;

  const load = () => {
    api<Axis[]>('/api/rating-axes').then(setAxes).catch(() => setAxes([]));
    api<SkillMap>('/api/coach/catalogue/skills').then(setMap).catch(() => setMap(null));
  };
  useEffect(() => { load(); }, []);
  // Each skill → a skill of the shared catalogue list («Оверхеды → смэш / оверхед»).
  const [map, setMap] = useState<SkillMap | null>(null);
  const sharedName = (key: string | null | undefined) => {
    const s = map?.shared.find((x) => x.key === key);
    return s ? (getLang() === 'ru' ? s.name_ru : s.name_en) : null;
  };

  async function create() {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      await api('/api/coach/rating-axes', { method: 'POST', body: JSON.stringify({ name, sport: both ? sport : coachSports[0] ?? 'padel' }) });
      haptic.success();
      setDraft(''); setAdding(false);
      load();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'axis_already_exists' ? t('co.axis.exists') : t('co.axis.failed'));
    } finally { setBusy(false); }
  }

  return (
    <PinnedSheet title={t('co.set.titleAxes')} onClose={onClose}
      footer={adding ? <>
        <button className="paybar__opt" onClick={() => { setAdding(false); setDraft(''); }}>{t('co.axis.cancel')}</button>
        <button className="savebtn" disabled={!draft.trim() || busy} onClick={create}>{t('co.axis.save')}</button>
      </> : <button className="savebtn" onClick={onClose}>{t('co.set.done')}</button>}>
      {both && (
        <div className="bseg" role="radiogroup" aria-label={t('co.set.sport')} style={{ margin: '0 0 10px' }}>
          {(['padel', 'tennis'] as const).map((x) => (
            <button key={x} role="radio" aria-checked={sport === x} onClick={() => { haptic.tap(); setSport(x); }}>
              {t(x === 'padel' ? 'co.sport.padel' : 'co.sport.tennis')}
            </button>
          ))}
        </div>
      )}
      {!shown ? <div className="skeleton" /> : (
      <div className="setuptags">
        {shown.length === 0 && <div className="card__when">{t('co.axis.none')}</div>}
        {shown.map((a) => {
          const written = a.guides.filter((g) => g.en || g.ru).length;
          return (
            <button key={a.id} className="setuptags__row"
              onClick={() => { haptic.tap(); setEditing(a); }}>
              <span className="axisrow">
                {/* Both names are the coach's own words, never translated on the fly. */}
                <span className="axisrow__name">{a.name}</span>
                <span className="axisrow__ru">{a.name_ru ?? t('co.axis.noRu')}</span>
                {(() => {
                  const m = map?.axes.find((x) => x.id === a.id);
                  const n = sharedName(m?.shared_skill);
                  return n ? <span className="axisrow__shared">{t('co.axis.shared', { skill: n })}{m?.source === 'ai' ? ` · ${t('co.axis.sharedAi')}` : ''}</span> : null;
                })()}
              </span>
              <span className="setuptags__count">
                {written === BANDS.length ? t('co.axis.guideAll') : t('co.axis.guideSome', { n: written, total: BANDS.length })}
              </span>
              <span className="setuptags__chev" aria-hidden="true">›</span>
            </button>
          );
        })}

        {adding ? (
          <div className="tagnew">
            <label className="mf__label" htmlFor="axis-new">{t('co.axis.new')}</label>
            <input id="axis-new" className="notearea" style={{ minHeight: 0 }} autoFocus
              placeholder={t('co.axis.newPlaceholder')} value={draft}
              onChange={(e) => setDraft(e.target.value)} />
          </div>
        ) : (
          <button className="setuptags__add" onClick={() => { haptic.tap(); setAdding(true); }}>
            {t('co.axis.newSkill')}
          </button>
        )}
      </div>
      )}

      {editing && (
        <AxisEditor axis={editing} map={map}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </PinnedSheet>
  );
}

/** One skill: its two names, and the line a coach wants read at each band. */
function AxisEditor({ axis, map, onClose, onSaved }: {
  axis: Axis; map: SkillMap | null; onClose: () => void; onSaved: () => void;
}) {
  const t = useT();
  const startShared = map?.axes.find((x) => x.id === axis.id)?.shared_skill ?? '';
  const [shared, setShared] = useState(startShared);
  const [name, setName] = useState(axis.name);
  const [nameRu, setNameRu] = useState(axis.name_ru ?? '');
  const [lines, setLines] = useState<Record<number, { en: string; ru: string }>>(() => {
    const start: Record<number, { en: string; ru: string }> = {};
    for (const [band] of BANDS) {
      const g = axis.guides.find((x) => x.band === band);
      start[band] = { en: g?.en ?? '', ru: g?.ru ?? '' };
    }
    return start;
  });
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState<'name' | 'guides' | null>(null);

  async function ai<T>(run: () => Promise<T>, what: 'name' | 'guides'): Promise<T | null> {
    setThinking(what);
    try { return await run(); }
    catch (err: any) {
      haptic.error();
      await alert(err.message === 'ai_not_configured' ? t('co.axis.aiOff') : t('co.axis.aiFailed'));
      return null;
    } finally { setThinking(null); }
  }

  async function suggest() {
    const res = await ai(() => api<{ suggestion: string }>(
      `/api/coach/rating-axes/${axis.id}/translate`,
      { method: 'POST', body: JSON.stringify({ into: 'ru' }) }), 'name');
    if (res) { setNameRu(res.suggestion); haptic.success(); }
  }

  async function draftAll() {
    const res = await ai(() => api<{ guides: { band: number; en: string; ru: string }[] }>(
      `/api/coach/rating-axes/${axis.id}/guides/draft`, { method: 'POST', body: '{}' }), 'guides');
    if (!res) return;
    const next = { ...lines };
    for (const g of res.guides) next[g.band] = { en: g.en, ru: g.ru };
    setLines(next);
    haptic.success();
  }

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await api(`/api/coach/rating-axes/${axis.id}`, {
        method: 'PUT', body: JSON.stringify({ name: name.trim(), nameRu: nameRu.trim() || null }),
      });
      await api(`/api/coach/rating-axes/${axis.id}/guides`, {
        method: 'PUT',
        body: JSON.stringify({
          guides: BANDS.map(([band]) => ({ band, en: lines[band].en, ru: lines[band].ru })),
        }),
      });
      if (map && shared !== startShared) {
        await api(`/api/coach/catalogue/skills/${axis.id}`, { method: 'PUT', body: JSON.stringify({ key: shared || null }) });
      }
      haptic.success();
      onSaved();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'axis_already_exists' ? t('co.axis.exists') : t('co.axis.failed'));
    } finally { setBusy(false); }
  }


  return (
    <PinnedSheet title={t('co.axis.edit')} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.axis.cancel')}</button>
        <button className="savebtn" disabled={busy || !name.trim()} onClick={save}>
          {busy ? t('co.axis.saving') : t('co.axis.save')}
        </button>
      </>}>
        <label className="mf">
          <span className="mf__label">{t('co.axis.nameEn')}</span>
          <input id="axis-name-en" value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="mf" style={{ marginTop: 10 }}>
          <span className="mf__label">{t('co.axis.nameRu')}</span>
          <div className="axisname">
            <input id="axis-name-ru" value={nameRu} placeholder={t('co.axis.nameRuPlaceholder')}
              onChange={(e) => setNameRu(e.target.value)} />
            <button className="aibtn" disabled={thinking !== null || !name.trim()} onClick={suggest}>
              {thinking === 'name' ? t('co.axis.thinking') : t('co.axis.suggest')}
            </button>
          </div>
        </label>

        {map && (
          <label className="mf" style={{ marginTop: 10 }}>
            <span className="mf__label">{t('co.axis.sharedLabel')}</span>
            <select className="notearea axis-shared" value={shared} onChange={(e) => setShared(e.target.value)}>
              <option value="">—</option>
              {map.shared.map((x) => <option key={x.key} value={x.key}>{getLang() === 'ru' ? x.name_ru : x.name_en}</option>)}
            </select>
          </label>
        )}

        <div className="axisguides__head">
          <span className="sheet__label" style={{ margin: 0 }}>{t('co.axis.guides')}</span>
          <button className="aibtn" disabled={thinking !== null} onClick={draftAll}>
            {thinking === 'guides' ? t('co.axis.drafting') : t('co.axis.draft')}
          </button>
        </div>

        {BANDS.map(([band, key]) => (
          <div className="axisband" key={band}>
            <div className="axisband__head">
              <span className="axisband__num">{band}–{band + 1}</span>
              <span className="axisband__word">{t(key)}</span>
            </div>
            <GrowArea className="axisband__line" rows={1} value={lines[band].en}
              placeholder={t('co.axis.linePlaceholderEn')}
              onChange={(e) => setLines({ ...lines, [band]: { ...lines[band], en: e.target.value } })} />
            <GrowArea className="axisband__line" rows={1} value={lines[band].ru}
              placeholder={t('co.axis.linePlaceholderRu')}
              onChange={(e) => setLines({ ...lines, [band]: { ...lines[band], ru: e.target.value } })} />
          </div>
        ))}

    </PinnedSheet>
  );
}
